import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create, Delete } from "@mui/icons-material";

import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { getEmptyTemplate } from "@dzambalaorg/mui-inputs-asset";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { formatItem } from "@framework/exchange";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { IMerge, MergeStatus, TokenType } from "@framework/types";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { Button, FormGrid, List } from "@dzambalaorg/ui-kit";

import { AdminDialogKeys } from "../../../../../shared";
import { mergeCreateFilter, mergeEditFilter, prepareMergeInitialValues } from "./schemas";
import { mergeValidationSchema } from "./form/validation";

const MERGE_URL = "/recipes/merge";
const emptyValues = {
  price: getEmptyTemplate(TokenType.ERC721),
  item: getEmptyTemplate(TokenType.ERC721),
  mergeStatus: MergeStatus.ACTIVE,
};

export const Merge = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IMerge>
  >(MERGE_URL, { enableInfiniteScroll: isMd }, void 0, {
    mergeStatus: [MergeStatus.ACTIVE],
  });

  const { openDialog } = useGlobalDialogFormCallbacks<IMerge, IMerge>();
  const { handleDelete } = useDialogDelete<IMerge>();

  const craftCreate = () => {
    openDialog(AdminDialogKeys.MERGE_CREATE, emptyValues as IMerge, {
      refetch,
      initialValues: emptyValues as IMerge,
      message: "dialogs.create",
      testId: "MergeFormDialog",
      filter: mergeCreateFilter,
      url: MERGE_URL,
      validationSchema: mergeValidationSchema,
    });
  };

  const craftEdit = (merge: IMerge) => () => {
    openDialog(AdminDialogKeys.MERGE_EDIT, merge, {
      refetch,
      initialValues: merge,
      message: "dialogs.edit",
      testId: "MergeFormDialog",
      url: `${MERGE_URL}/${merge.id}`,
      validationSchema: mergeValidationSchema,
      filter: mergeEditFilter,
      prepareInitialValues: prepareMergeInitialValues,
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "recipes", "recipes.merge"]} />

      <PageHeader message="pages.recipes.merge.title">
        <Button intent="secondary" startIcon={<Add />} onClick={craftCreate} data-testid="MergeCreateButton">
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <SearchWithFilters initialValues={{ mergeStatus: [MergeStatus.ACTIVE] }}>
        <FormGrid
          columns={[1]}
          containerProps={{ sx: theme => ({ marginTop: theme.spacing(theme.tokens.spacing.xl) }) }}
        >
          <SelectInput multiple name="mergeStatus" options={MergeStatus} />
        </FormGrid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(merge => (
            <StyledListItem key={merge.id}>
              <List.ItemText sx={{ flex: "0 1 45%" }}>{formatItem(merge.price)}</List.ItemText>
              <List.ItemText sx={{ flex: "0 1 45%" }}>{formatItem(merge.item)}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={craftEdit(merge)}
                  message="form.buttons.edit"
                  dataTestId="MergeEditButton"
                  icon={Create}
                />
                <ListAction
                  onClick={handleDelete(merge)}
                  message="form.buttons.delete"
                  dataTestId="MergeDeleteButton"
                  icon={Delete}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={MERGE_URL} refetch={refetch} />
    </Grid>
  );
};
